/**
 * The Data Quality tab — full width, deep-linkable, source- and view-aware.
 *
 * `AVAILABLE_VIEWS` is the single place the view set grows, so the command bar,
 * the URL validation and what actually renders can never fall out of sync. A
 * `dqView` naming a view that is not in this list degrades to the first one.
 *
 * Compare is the one view still to come; it needs no new payload and is not a
 * parity gap.
 */

import { AlertTriangle, ShieldCheck } from 'lucide-react';
import NoDataState from '../../ui/NoDataState';
import { useValidationSummary } from '../hooks/useDataQuality';
import ConsoleCommandBar from './ConsoleCommandBar';
import { orderChains, sectionHeading } from './dqSelectors';
import { useDataQualityConsole, type ConsoleView } from './hooks/useDataQualityConsole';
import FieldsView from './views/FieldsView';
import FindingsView from './views/FindingsView';
import SummaryView from './views/SummaryView';

export interface DataQualityConsoleProps {
  companyId: string | null | undefined;
}

const AVAILABLE_VIEWS: ConsoleView[] = ['summary', 'findings', 'fields'];

function CommandBarSkeleton() {
  return (
    <div className="animate-pulse space-y-3 border-b border-neutral-200 pb-3">
      <div className="h-8 w-64 rounded-lg bg-neutral-100" />
      <div className="h-3 w-40 rounded bg-neutral-100" />
    </div>
  );
}

function TileSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-24 animate-pulse rounded-xl border border-neutral-200 bg-neutral-50" />
      ))}
    </div>
  );
}

/**
 * What the console would like, not what it gets.
 *
 * The backend route accepts only `source`, `include_previous` and `view`, and
 * calls `ui_summary()` with no argument — so the issue list is capped at its
 * Python default of 5 for every caller, and this param is ignored today. It is
 * sent anyway because it costs nothing and the cap lifts the day the param
 * lands; until then `issue_count` is the true total and the Findings view says
 * so plainly rather than pretending the five it holds are all of them.
 */
const CONSOLE_MAX_ISSUES = 50;

export default function DataQualityConsole({ companyId }: DataQualityConsoleProps) {
  const { data, isLoading, isError } = useValidationSummary(
    companyId,
    true,
    CONSOLE_MAX_ISSUES
  );
  const ordered = orderChains(data?.chains ?? []);

  const state = useDataQualityConsole(data?.chains, AVAILABLE_VIEWS);

  if (!companyId) return null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <CommandBarSkeleton />
        <TileSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-10 text-center">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <p className="text-sm text-amber-800">
          Data quality is temporarily unavailable. This does not affect the figures shown elsewhere on this page.
        </p>
      </div>
    );
  }

  // An empty state IS correct here — the reader deliberately opened this tab,
  // unlike the Overview strip, which stays silent instead.
  if (ordered.length === 0) {
    return (
      <NoDataState
        icon={ShieldCheck}
        title="No validation has been stored for this company"
        hint="Nothing has been checked yet, or storage of results is disabled for this deployment."
      />
    );
  }

  const chain = state.chain ?? ordered[0];

  return (
    <div className="space-y-4">
      <ConsoleCommandBar
        chains={ordered}
        source={state.source || chain.source}
        onSourceChange={state.setSource}
        view={state.view}
        onViewChange={state.setView}
        availableViews={AVAILABLE_VIEWS}
      />

      {data?.persistence_enabled === false && (
        <p className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
          Results are not being stored for this deployment — trend comparison
          against a previous run is unavailable.
        </p>
      )}

      {/* Which body of data this is. The command bar's run/document line does
          not answer that, and a reader with two sources needs it named. */}
      <h3 className="border-b border-neutral-200 pb-1.5 text-xs font-bold uppercase tracking-widest text-neutral-500">
        {sectionHeading(chain)}
      </h3>

      {state.view === 'summary' && (
        // One update, not three: see `drillIntoYear`.
        <SummaryView chain={chain} onSelectYear={state.drillIntoYear} />
      )}

      {state.view === 'findings' && (
        <FindingsView
          companyId={companyId}
          chain={chain}
          filter={state.filter}
          selectedCode={state.selectedCode}
          onSelectCode={state.setSelectedCode}
          onCloseDossier={() => state.setSelectedCode(null)}
          onToggleSeverity={state.toggleSeverity}
          onToggleFamily={state.toggleFamily}
          onToggleYear={state.toggleYear}
          onToggleVerdict={state.toggleVerdict}
          onClearFilter={state.clearFilter}
          onSelectField={state.showField}
        />
      )}

      {state.view === 'fields' && (
        <FieldsView companyId={companyId} chain={chain} highlightField={state.field} />
      )}
    </div>
  );
}
